import type { OnboardingPurpose } from "@solomon-apps/shared/src/types/clientOnboardingPolicy";

import {
    EMPTY_LEGAL_ENTITY_TYPE_DRAFT,
    type LegalEntityTypeDraft,
    legalEntityTypeDraftFromValue,
    legalEntityTypeValue
} from "@/data/legalEntityTypes";
import {
    BACKEND_TO_CAPITAL_RANGE,
    BACKEND_TO_USE_CASE,
    CAPITAL_RANGE_TO_BACKEND,
    type CapitalRange,
    USE_CASE_TO_BACKEND,
    type UseCaseId
} from "@/pages/Onboarding/forms/useCaseFormShared";
import type {
    IncorporationJurisdiction,
    LegalEntityPayload,
    LiteDeclaredAddressesPayload,
    LiteOnboardingPayload,
    OnboardingApplicationStatus,
    OnboardingStepView,
    SignatoryAuthorityPayload,
    UseCaseAndIntentPayload
} from "@/services/clientOnboarding";

/**
 * Pure logic for the lite (single-step MVP) onboarding form, colocated with
 * `OnboardingLite.tsx`. Kept DOM-free so it is cheap to unit-test (gating,
 * payload building, prefill decode) without rendering the component.
 */
/**
 * One row of the Authorized Address Register (Order Form §2.3). The register
 * is nine columns; the six the applicant cannot answer are fixed per pathway
 * by Operator in Annex X.5, so only these three are asked for here.
 */
export interface LiteWalletDraft {
    /**
     * Stable row identity. Used for React keys and to target the row being
     * edited in place, so neither depends on array position (a remove would
     * shift every index below it). Assigned by `nextLiteWalletId` when the
     * address is added; the blank add-form draft carries an empty id until
     * then, or by `prefillLiteForm` for a row read back from the server.
     * Never persisted.
     */
    id: string;
    address: string;
    label: string;
    primaryAssets: string[];
}

export const EMPTY_LITE_WALLET: LiteWalletDraft = {
    id: "",
    address: "",
    label: "",
    primaryAssets: []
};

let liteWalletIdSeq = 0;

/** Mint a row id. Only has to be unique within one mounted form, so a
 *  module-local counter is enough and keeps the value deterministic. */
export function nextLiteWalletId(): string {
    liteWalletIdSeq += 1;
    return `lite-wallet-${liteWalletIdSeq}`;
}

/**
 * The one upload lite asks for. `filename` is what the slot captions: null
 * for a document read back from a prior save, whose payload stores only
 * the uuid, until the page resolves it from the documents list.
 */
export interface LiteFormationDocument {
    documentUuid: string;
    filename: string | null;
}

export interface LiteFormState {
    companyName: string;
    legalName: string;
    /** Parties clause of both TPL-00001 and TPL-00003. */
    entityType: LegalEntityTypeDraft;
    jurisdiction: IncorporationJurisdiction | null;
    registeredAddress: string;
    companyNumber: string;
    /** The entity's formation document, uploaded before the submit. */
    formationDocument: LiteFormationDocument | null;
    primaryContactSameAsSignatory: boolean;
    /** Separate contact draft, retained while the shortcut is selected. */
    primaryContactName: string;
    primaryContactEmail: string;
    /** Order Form §2.3, one entry per declared address. */
    wallets: LiteWalletDraft[];
    /** The individual who signs on the entity's behalf. */
    signatoryName: string;
    signatoryEmail: string;
    signatoryTitle: string;
    useCases: Set<UseCaseId>;
    capitalRange: CapitalRange | null;
    /** Terms of Service acceptance for this submission. */
    termsAccepted: boolean;
}

export const EMPTY_LITE_FORM_STATE: LiteFormState = {
    companyName: "",
    legalName: "",
    entityType: EMPTY_LEGAL_ENTITY_TYPE_DRAFT,
    jurisdiction: null,
    registeredAddress: "",
    companyNumber: "",
    formationDocument: null,
    primaryContactSameAsSignatory: true,
    primaryContactName: "",
    primaryContactEmail: "",
    // The register starts empty: rows land in state only once the applicant
    // adds one, so an untouched form has nothing to validate row-by-row.
    wallets: [],
    signatoryName: "",
    signatoryEmail: "",
    signatoryTitle: "",
    useCases: new Set<UseCaseId>(),
    capitalRange: null,
    termsAccepted: false
};

/**
 * Whether this application signs the Minting Master Agreement, which is the
 * only agreement the Authorized Mint & Redeem Addresses print on. The
 * server composes a `minting_agreement_signing` step into the application
 * exactly when the issuer has enabled Mint and Redeem for the client, and
 * every other surface (the full hub's rail, the issuer review, the API's
 * approve gate) reads the same row as that gate, so the lite form does too.
 * An absent step list reads as MSA-only, the safe direction: nothing is
 * asked for that the client cannot be held to.
 */
export function liteSignsMintingAgreement(
    steps: ReadonlyArray<Pick<OnboardingStepView, "stepName">> | undefined
): boolean {
    return steps?.some((step) => step.stepName === "minting_agreement_signing") ?? false;
}

/** Requirements come from the server-owned application purpose. An omitted
 * value is treated as platform so an older response never weakens the form. */
export function isMintingOnlyApplication(purpose: OnboardingPurpose | undefined): boolean {
    return purpose === "minting_only";
}

// Permissive client-side email check; the backend schema enforces the
// canonical rule. Good enough to gate the submit button.
export function isValidEmail(value: string): boolean {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());
}

/** The lite form's required fields, in display order. Drives both the
 *  per-field validation messages and the scroll-to-first-invalid on a
 *  blocked submit attempt. */
export const LITE_REQUIRED_FIELD_ORDER = [
    "legalName",
    "entityType",
    "jurisdiction",
    "registeredAddress",
    "companyNumber",
    "formationDocument",
    "signatoryName",
    "signatoryEmail",
    "signatoryTitle",
    "primaryContactName",
    "primaryContactEmail",
    "wallets",
    "useCases",
    "termsAccepted"
] as const;
export type LiteRequiredField = (typeof LITE_REQUIRED_FIELD_ORDER)[number];

/**
 * Per-field required-field messages (mirrors PRD D2 + AC8). Empty object
 * means the form is submittable. Contact name is required for minting
 * requests; capital range stays optional. The formation document and the Terms of
 * Service are required like the rest: the issuer reviews the entity
 * against the document, and nothing is submitted the applicant has not
 * agreed to. `signsMintingAgreement` is
 * `liteSignsMintingAgreement` for the application: the address register is
 * only asked for, and so only required, when the client signs the Minting
 * Master Agreement it prints on.
 */
export function liteFieldErrors(
    state: LiteFormState,
    signsMintingAgreement: boolean,
    purpose?: OnboardingPurpose
): Partial<Record<LiteRequiredField, string>> {
    const errors: Partial<Record<LiteRequiredField, string>> = {};
    if (state.legalName.trim().length === 0) {
        errors.legalName = "Enter your company's legal name.";
    }
    if (state.jurisdiction === null) {
        errors.jurisdiction = "Select where the entity was incorporated.";
    }
    if (
        (signsMintingAgreement || isMintingOnlyApplication(purpose)) &&
        !state.primaryContactSameAsSignatory &&
        state.primaryContactName.trim().length === 0
    ) {
        errors.primaryContactName = "Enter your primary contact name.";
    }
    if (!state.primaryContactSameAsSignatory && state.primaryContactEmail.trim().length === 0) {
        errors.primaryContactEmail = "Enter your primary contact email.";
    } else if (!state.primaryContactSameAsSignatory && !isValidEmail(state.primaryContactEmail)) {
        errors.primaryContactEmail = "Enter a valid email address.";
    }
    if (!isMintingOnlyApplication(purpose) && state.useCases.size === 0) {
        errors.useCases = "Pick at least one use case.";
    }
    if (!isMintingOnlyApplication(purpose) && legalEntityTypeValue(state.entityType).length === 0) {
        errors.entityType =
            state.entityType.selection === "other" ? "Enter the entity's type." : "Select the entity's type.";
    }
    if (state.registeredAddress.trim().length === 0) {
        errors.registeredAddress = "Enter the registered address.";
    }
    if (state.companyNumber.trim().length === 0) {
        errors.companyNumber = "Enter the entity's registration number.";
    }
    if (state.formationDocument === null) {
        errors.formationDocument = "Upload the entity's formation document.";
    }
    // One complete row is the minimum: minting is restricted to registered
    // addresses, so an application with none has nothing to authorize.
    // Without the minting agreement there is no register to fill.
    if (signsMintingAgreement) {
        const usableWallets = state.wallets.filter((w) => w.address.trim().length > 0 || w.primaryAssets.length > 0);
        if (usableWallets.length === 0) {
            errors.wallets = "Add at least one address.";
        } else if (usableWallets.some((w) => w.address.trim().length === 0)) {
            errors.wallets = "Enter an address for every entry, or remove it.";
        } else if (usableWallets.some((w) => w.primaryAssets.length === 0)) {
            errors.wallets = "Pick the assets each address mints against.";
        }
    }
    if (state.signatoryName.trim().length === 0) {
        errors.signatoryName = "Enter the signatory's legal name.";
    }
    if (state.signatoryEmail.trim().length === 0) {
        errors.signatoryEmail = "Enter the signatory's email.";
    } else if (!isValidEmail(state.signatoryEmail)) {
        errors.signatoryEmail = "Enter a valid email address.";
    }
    if (state.signatoryTitle.trim().length === 0) {
        errors.signatoryTitle = "Enter the signatory's title.";
    }
    if (!state.termsAccepted) {
        errors.termsAccepted = "Accept the Terms of Service to submit.";
    }
    return errors;
}

/** Required-field gate for the lite submit. */
export function isLiteSubmittable(
    state: LiteFormState,
    signsMintingAgreement: boolean,
    purpose?: OnboardingPurpose
): boolean {
    return Object.keys(liteFieldErrors(state, signsMintingAgreement, purpose)).length === 0;
}

/**
 * Build the backend payload from form state, or `null` if not submittable.
 * Every field the form asks for is sent: the agreement fields land on the
 * same step rows the full flow uses, and the register on the
 * `wallet_declarations` step as declared addresses. A client that signs no
 * minting agreement was never shown the register, so none is sent: rows
 * that could only have come from a stale render must not be declared.
 */
export function buildLitePayload(
    state: LiteFormState,
    signsMintingAgreement: boolean,
    purpose?: OnboardingPurpose
): LiteOnboardingPayload | null {
    if (
        !isLiteSubmittable(state, signsMintingAgreement, purpose) ||
        state.jurisdiction === null ||
        state.formationDocument === null ||
        !state.termsAccepted
    ) {
        return null;
    }
    return {
        // Display Name is only the label shown across the app, so the legal
        // name is the stand-in when it is left blank.
        companyName: state.companyName.trim() || state.legalName.trim(),
        legalName: state.legalName.trim(),
        ...(!isMintingOnlyApplication(purpose) ? { entityType: legalEntityTypeValue(state.entityType) } : {}),
        incorporationJurisdiction: state.jurisdiction,
        registeredAddress: state.registeredAddress.trim(),
        companyNumber: state.companyNumber.trim(),
        formationDocumentUuid: state.formationDocument.documentUuid,
        primaryContactSameAsSignatory: state.primaryContactSameAsSignatory,
        primaryContactEmail: (state.primaryContactSameAsSignatory
            ? state.signatoryEmail
            : state.primaryContactEmail
        ).trim(),
        primaryContactName:
            (state.primaryContactSameAsSignatory ? state.signatoryName : state.primaryContactName).trim() || null,
        signatoryName: state.signatoryName.trim(),
        signatoryEmail: state.signatoryEmail.trim(),
        signatoryTitle: state.signatoryTitle.trim(),
        wallets: signsMintingAgreement
            ? state.wallets.map((wallet) => ({
                  address: wallet.address.trim(),
                  label: wallet.label.trim() || null,
                  primaryAssets: wallet.primaryAssets
              }))
            : [],
        useCases: Array.from(state.useCases).map((id) => USE_CASE_TO_BACKEND[id]),
        expectedCapitalRange: state.capitalRange ? CAPITAL_RANGE_TO_BACKEND[state.capitalRange] : null,
        termsAccepted: true
    };
}

interface OrgIdentity {
    name: string;
    legalName: string | null;
}

/**
 * Seed the form from the org row (source of truth for the names) plus the
 * application row + step payloads (jurisdiction, contact, intent), so a reload
 * after a prior lite save shows the captured values (AC4). Pure given inputs.
 * A formation document read back from the Legal Entity payload comes with
 * no filename (the payload stores the uuid alone); the page resolves it. A
 * prior acceptance of the Terms of Service is shown as still given, so a
 * resubmit after a send-back does not ask twice.
 */
export function prefillLiteForm(
    org: OrgIdentity | null,
    application: {
        incorporationJurisdiction: IncorporationJurisdiction | null;
        termsAcceptedAt?: string | null;
    } | null,
    steps: OnboardingStepView[] | undefined
): LiteFormState {
    const legalStep = steps?.find((s) => s.stepName === "legal_entity");
    const useCaseStep = steps?.find((s) => s.stepName === "use_case_and_intent");
    const signatoryStep = steps?.find((s) => s.stepName === "signatory_authority");
    const walletStep = steps?.find((s) => s.stepName === "wallet_declarations");
    const lp = (legalStep?.payload ?? {}) as Partial<LegalEntityPayload>;
    const up = (useCaseStep?.payload ?? {}) as Partial<UseCaseAndIntentPayload>;
    const sp = (signatoryStep?.payload ?? {}) as Partial<SignatoryAuthorityPayload>;
    const wp = (walletStep?.payload ?? {}) as Partial<LiteDeclaredAddressesPayload>;
    // Rows read back from a prior lite save get fresh ids: the server stores
    // no row identity, and the form only needs one that is stable while
    // mounted.
    const wallets: LiteWalletDraft[] = Array.isArray(wp.declaredAddresses)
        ? wp.declaredAddresses.map((row) => ({
              id: nextLiteWalletId(),
              address: row.address,
              label: row.label ?? "",
              primaryAssets: row.primaryAssets
          }))
        : [];

    const useCases = new Set<UseCaseId>(
        Array.isArray(up.useCases)
            ? up.useCases.map((b) => BACKEND_TO_USE_CASE[b]).filter((v): v is UseCaseId => Boolean(v))
            : []
    );

    return {
        companyName: (org?.name ?? "").trim(),
        // Legal Name prefills only from a stored legal name (org.legalName, or a
        // prior lite save). No fallback to the company name: when no legal name
        // is set, the field stays blank for the applicant to fill in.
        legalName: (org?.legalName ?? lp.legalName ?? "").trim(),
        jurisdiction: application?.incorporationJurisdiction ?? lp.incorporationJurisdiction ?? null,
        entityType: legalEntityTypeDraftFromValue(lp.entityType),
        registeredAddress: lp.registeredAddress ?? "",
        companyNumber: lp.companyNumber ?? "",
        formationDocument: lp.formationDocumentUuid ? { documentUuid: lp.formationDocumentUuid, filename: null } : null,
        primaryContactSameAsSignatory:
            lp.primaryContactSameAsSignatory ??
            ((!(lp.primaryContactName ?? "").trim() || lp.primaryContactName?.trim() === sp.signatoryName?.trim()) &&
                (!(lp.primaryContactEmail ?? "").trim() ||
                    lp.primaryContactEmail?.trim() === sp.signatoryEmail?.trim())),
        primaryContactName: lp.primaryContactName ?? "",
        primaryContactEmail: lp.primaryContactEmail ?? "",
        wallets,
        signatoryName: sp.signatoryName ?? "",
        signatoryEmail: sp.signatoryEmail ?? "",
        signatoryTitle: sp.signatoryTitle ?? "",
        useCases,
        capitalRange: up.expectedCapitalRange ? (BACKEND_TO_CAPITAL_RANGE[up.expectedCapitalRange] ?? null) : null,
        termsAccepted: Boolean(application?.termsAcceptedAt)
    };
}

export type LiteScreen = "form" | "pending_review" | "agreements_out" | "approved";

/**
 * Which screen the lite page shows. The server's application status wins:
 * a poll that comes back `awaiting_agreements` or `approved` must take the
 * page forward even if this visit is the one that submitted. The
 * `submittedThisVisit` latch only covers the gap between a successful
 * submit and the refetch that carries the new status, so the form does
 * not flash back while the cache catches up. A rejected application shows
 * the form again, with the issuer's reason, so the applicant can resubmit.
 */
export function liteScreen(status: OnboardingApplicationStatus | null, submittedThisVisit: boolean): LiteScreen {
    switch (status) {
        case "approved":
            return "approved";
        case "awaiting_agreements":
            return "agreements_out";
        case "in_review":
            return "pending_review";
        case "draft":
        case "rejected":
        case null:
            return submittedThisVisit ? "pending_review" : "form";
    }
}
